import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Swal from "sweetalert2";
import TopNavbar from "../components/Nav/TopNavbar";
import Footer from "../components/Sections/Footer";
import ServiceAPI from "../services/NavaService";

export default function UsersAdmin() {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 12;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userItem, setUserItem] = useState(null);
  const [editedAdminYn, setEditedAdminYn] = useState("N");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const customAlert = (text, icon = "info") => {
    Swal.fire({
      text,
      icon,
      didOpen: () => {
        const container = document.querySelector(".swal2-container");
        const popup = document.querySelector(".swal2-popup");
        if (container) container.style.zIndex = "10010";
        if (popup) popup.style.zIndex = "10011";
      },
    });
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await ServiceAPI.getAllUsers();
      const list = Array.isArray(res.data) ? res.data : [];
      const normalized = list.map((u) => ({
        ...u,
        nurId: u.nurId ?? u.id ?? u.nur_id ?? null,
        usrId: u.usrId ?? u.userId ?? u.usr_id ?? null,
        firstName: u.firstName ?? u.first_name ?? "",
        middleName: u.middleName ?? u.middle_name ?? "",
        lastName: u.lastName ?? u.last_name ?? "",
        email: u.email ?? "",
        adminYn: (u.adminYn ?? u.admin_yn ?? "N") || "N",
        createdBy: u.createdBy ?? u.created_by ?? null,
        createdDate: u.createdDate ?? u.created_date ?? null,
        modifiedBy: u.modifiedBy ?? u.modified_by ?? null,
        modifiedDate: u.modifiedDate ?? u.modified_date ?? null,
      }));
      setUsers(normalized);
    } catch (err) {
      console.error(err);
      customAlert("Failed to load users", "error");
      setUsers([]);
    } finally {
      setLoading(false);
      setPage(1);
    }
  };

  const filtered = (users || []).filter((u) => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return true;
    const fields = [
      u.firstName ?? "",
      u.middleName ?? "",
      u.lastName ?? "",
      u.email ?? "",
      (u.usrId ?? "").toString(),
      (u.nurId ?? "").toString(),
    ];
    return fields.join(" ").toLowerCase().includes(q);
  });

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);

  const openViewModal = (u) => {
    setUserItem(u);
    setEditedAdminYn(u.adminYn ?? "N");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setUserItem(null);
    setEditedAdminYn("N");
    setIsSaving(false);
  };

  const getCurrentUsername = () => {
    const raw = localStorage.getItem("user");
    const user = raw ? JSON.parse(raw) : null;
    return user?.username ?? user?.name ?? "unknown";
  };

  const handleSave = async () => {
    if (!userItem) return;
    const idForUpdate = userItem.nurId ?? userItem.usrId ?? userItem.id ?? null;
    if (!idForUpdate) {
      customAlert("User id missing", "error");
      return;
    }

    const confirm = await Swal.fire({
      title: "Save changes?",
      text: "You are about to save changes to this user.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Save",
      cancelButtonText: "Cancel",
      didOpen: () => {
        const container = document.querySelector(".swal2-container");
        const popup = document.querySelector(".swal2-popup");
        if (container) container.style.zIndex = "10010";
        if (popup) popup.style.zIndex = "10011";
      },
    });
    if (!confirm.isConfirmed) return;

    setIsSaving(true);

    try {
      const payload = {
        ...(userItem.nurId !== undefined && userItem.nurId !== null ? { nurId: userItem.nurId } : {}),
        ...(userItem.usrId !== undefined && userItem.usrId !== null ? { usrId: userItem.usrId } : {}),
        adminYn: editedAdminYn === "Y" ? "Y" : "N",
        email: userItem.email ?? "",
        firstName: userItem.firstName ?? "",
        middleName: userItem.middleName ?? "",
        lastName: userItem.lastName ?? "",
        ...(userItem.createdBy ? { createdBy: userItem.createdBy } : {}),
        ...(userItem.createdDate ? { createdDate: userItem.createdDate } : {}),
        modifiedBy: getCurrentUsername(),
        modifiedDate: new Date().toISOString(),
      };

      await ServiceAPI.updateUserRole(idForUpdate, payload);

      customAlert("User role updated", "success");
      await loadUsers();
      closeModal();
    } catch (err) {
      console.error(err);
      customAlert("Failed to update user", "error");
      setIsSaving(false);
    }
  };

  return (
    <>
      <TopNavbar />
      <PageWrapper style={{ marginTop: "5%" }}>
        <TopRow>
          <h2>Users</h2>
          <div className="controls">
            <input
              type="text"
              placeholder="Search name, email or id..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            <OutlineBtn onClick={() => { setSearch(""); setPage(1); }}>Clear</OutlineBtn>
          </div>
        </TopRow>

        <TableWrap>
          {loading ? (
            <div>Loading…</div>
          ) : (
            <>
              <table>
                <thead>
                  <tr>
                    <th>usrId</th>
                    <th>First name</th>
                    <th>Middle name</th>
                    <th>Last name</th>
                    <th>Email</th>
                    <th>Admin</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((u) => {
                    const uid = u.usrId ?? u.nurId ?? u.id ?? "-";
                    return (
                      <tr key={uid}>
                        <td>{uid}</td>
                        <td>{u.firstName}</td>
                        <td>{u.middleName}</td>
                        <td>{u.lastName}</td>
                        <td title={u.email}>{u.email}</td>
                        <td>{u.adminYn === "Y" ? "Yes" : "No"}</td>
                        <td className="actions">
                          <PrimarySmall onClick={() => openViewModal(u)}>View</PrimarySmall>
                        </td>
                      </tr>
                    );
                  })}

                  {pageItems.length === 0 && (
                    <tr>
                      <td colSpan="7">No users found.</td>
                    </tr>
                  )}
                </tbody>
              </table>

              <Pagination>
                <OutlineSmall disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</OutlineSmall>
                <span>Page {page} / {pageCount}</span>
                <OutlineSmall disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>Next</OutlineSmall>
              </Pagination>
            </>
          )}
        </TableWrap>

        {/* Modal */}
        {isModalOpen && userItem && (
          <ModalOverlay onClick={closeModal}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <h3>View User</h3>
                <button onClick={closeModal}>Close</button>
              </ModalHeader>

              <ModalBody>

                <label>First name</label>
                <input type="text" value={userItem.firstName ?? ""} readOnly />

                <label>Middle name</label>
                <input type="text" value={userItem.middleName ?? ""} readOnly />

                <label>Last name</label>
                <input type="text" value={userItem.lastName ?? ""} readOnly />

                <label>Email</label>
                <input type="text" value={userItem.email ?? ""} readOnly />

                <label>Admin</label>
                <select value={editedAdminYn} onChange={(e) => setEditedAdminYn(e.target.value)}>
                  <option value="Y">Yes</option>
                  <option value="N">No</option>
                </select>

                <label>Created By</label>
                <input type="text" value={userItem.createdBy ?? ""} readOnly />

                <label>Created Date</label>
                <input type="text" value={userItem.createdDate ? new Date(userItem.createdDate).toLocaleString() : ""} readOnly />

                <label>Modified By</label>
                <input type="text" value={userItem.modifiedBy ?? ""} readOnly />

                <label>Modified Date</label>
                <input type="text" value={userItem.modifiedDate ? new Date(userItem.modifiedDate).toLocaleString() : ""} readOnly />
              </ModalBody>

              <ModalFooter>
                <OutlineBtn onClick={closeModal}>Close</OutlineBtn>
                <PrimaryBtn onClick={handleSave} disabled={isSaving}>
                  {isSaving ? "Saving..." : "Save changes"}
                </PrimaryBtn>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}
      </PageWrapper>
    </>
  );
}

/* Reuse same styles as MessagesAdmin / SalesAdmin so UI is consistent */
const PageWrapper = styled.div`
  padding: 24px;
  max-width: 1180px;
  margin: 0 auto;
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial;
  color: #222;
  h2 { margin: 0 0 12px 0; font-size:20px; font-weight:700; }
  .controls { display:flex; gap:10px; align-items:center; margin-bottom:12px; }
  input[type="text"] { padding:10px 12px; border:1px solid #e7e7f0; border-radius:8px; min-width:260px; }
`;

const TopRow = styled.div`
  display:flex; justify-content:space-between; align-items:center; margin-bottom:16px; gap:12px;

  h2 { margin:0; font-size:20px; }

  .controls { display:flex; gap:8px; align-items:center; }

  @media (max-width:720px) {
    flex-direction:column; align-items:flex-start;
    .controls { width:100%; display:flex; gap:8px; flex-wrap:wrap; }
  }
`;

const TableWrap = styled.div`
  background:#fff;
  border:1px solid #f0f1f6;
  border-radius:10px;
  max-height: calc(100vh - 320px);
  overflow: auto;
  -webkit-overflow-scrolling: touch;

  table {
    width:100%;
    border-collapse:collapse;
    font-size:14px;
    table-layout: fixed;
    min-width:900px;
  }

  thead th {
    position:sticky;
    top:0;
    background: linear-gradient(180deg,#fff,#fbfbfe);
    z-index: 3;
    padding:12px;
    text-align:left;
    font-weight:700;
    border-bottom:1px solid #f2f4f8;
  }

  th, td {
    padding:10px 12px;
    border-bottom:1px solid #f6f7fb;
    vertical-align: top;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  td[title] { cursor: help; }

  td.actions {
    white-space: nowrap;
    width: 140px;
  }

  tbody tr:hover { background:#fbfcff; }

  @media (max-width:1000px) {
    table { min-width: 820px; }
    th, td { padding:9px; font-size:13px; }
  }
`;

const Pagination = styled.div`
  display:flex;
  justify-content:center;
  align-items:center;
  gap:10px;
  padding:12px 0;
  font-size:14px;
`;

const ModalOverlay = styled.div`
  position:fixed; inset:0; background: rgba(6,8,20,0.48);
  display:flex; align-items:center; justify-content:center; z-index:5000; padding:16px;
`;

const ModalContent = styled.div`
  width:720px; max-width:96%; max-height:92vh; overflow:auto; background:#fff; border-radius:12px; padding:18px;
  box-shadow: 0 30px 80px rgba(18,24,64,0.16);
`;

const ModalHeader = styled.div`
  display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;
  h3 { margin:0; font-size:18px; font-weight:700; }
  button { background:transparent; border:0; color:#666; font-size:15px; cursor:pointer; }
`;

const ModalBody = styled.div`
  display:flex; flex-direction:column; gap:10px;
  label { font-weight:600; font-size:13px; margin-top:8px; color:#222; }
  input[type="text"], select, textarea { padding:10px; border:1px solid #e9e9f2; border-radius:8px; font-size:14px; }
  textarea { min-height:120px; resize:vertical; }
`;

const IdBox = styled.div`
  padding:8px 10px; background:#f7f8fb; border-radius:8px; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, "Roboto Mono", monospace;
`;

const ModalFooter = styled.div`
  display:flex; justify-content:flex-end; gap:10px; margin-top:12px;
`;

const PrimaryBtn = styled.button`
  padding:9px 12px;
  border-radius:8px;
  border:0;
  background: linear-gradient(180deg,#6a2cff,#5a22c9);
  color:#fff;
  cursor:pointer;
  font-weight:600;
  box-shadow: 0 8px 20px rgba(88,34,200,0.12);
`;

const OutlineBtn = styled.button`
  padding:9px 12px;
  border-radius:8px;
  border:1px solid #e9e9f4;
  background:#fff;
  color:#222;
  cursor:pointer;
`;

const PrimarySmall = styled.button`
  padding:7px 10px; border-radius:8px; border:0; background:#6a2cff; color:#fff; cursor:pointer; margin-right:6px; white-space:nowrap;
`;
const OutlineSmall = styled.button`
  padding:7px 10px; border-radius:8px; border:1px solid #e9e9f4; background:#fff; color:#222; cursor:pointer;
`;
